import type { NuxtLinkProps } from '#app'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

import { type Market, MarketCountryCode } from '@backmarket/http-api'

import { ROUTES } from '~/scopes/auth/route-names'

const COUNTRIES_WITH_ENGLISH_LOCALE = [
  MarketCountryCode.IE,
  MarketCountryCode.GB,
  MarketCountryCode.AU,
]

const COUNTRIES_WITH_REDIRECTION_TO_HOME_PAGE = [
  MarketCountryCode.AU,
  MarketCountryCode.JP,
]

const getBmRedirectUrl = (market: Market) => {
  if (COUNTRIES_WITH_REDIRECTION_TO_HOME_PAGE.includes(market.countryCode))
    return ''

  if (market.countryCode === MarketCountryCode.US)
    return 'l/student-discount/e24cb331-e521-4619-a5d9-19ac82ab8b1f'

  // for all European countries, we redirect users to this student deals PLP
  return 'l/bons-plans-etudiants/255c7460-e8ec-458b-b268-da8954d3b300'
}

/**
 * Depending on the country, we need to redirect the user to a specific page after a successful student verification on the SheerID page
 */
const buildBmRedirectLandingPage = (props: {
  currentRoute: RouteLocationNormalizedLoaded
  originUrl: string
  market: Market
}) => {
  const locale = props.currentRoute.params.locale as string

  const relativeUrl = `/${locale}/${getBmRedirectUrl(props.market)}`

  return new URL(relativeUrl, props.originUrl).href
}

function buildSheerIdUrl({
  sheerIdUrl,
  market,
  encryptedUserId,
  currentRoute,
  originUrl,
}: {
  sheerIdUrl: string
  market: Market
  encryptedUserId: string
  currentRoute: RouteLocationNormalizedLoaded
  originUrl: string
}) {
  const url = new URL(sheerIdUrl)
  url.searchParams.append('country', market.countryCode)

  // SheerID expects a lang as a locale... except for UK, Ireland and Australia where it expects a locale with this format: 'en-GB'
  // FIXME: is there no util to retrieve the current lang
  const lang = market.defaultLocale.split('_')[0]
  url.searchParams.append(
    'locale',
    COUNTRIES_WITH_ENGLISH_LOCALE.includes(market.countryCode) ? 'en-gb' : lang,
  )
  // note: sheerID is expecting a 'redirectURL' query param, not 'redirectUrl'
  url.searchParams.append(
    'redirectURL',
    buildBmRedirectLandingPage({ currentRoute, market, originUrl }),
  )
  url.searchParams.append('userid', encryptedUserId)

  return url.href
}

export function buildStudentVerificationUrl({
  currentRoute,
  market,
  encryptedUserId = 'USER_ID',
  sheerIdUrl,
  authenticated,
  originUrl,
}: {
  currentRoute: RouteLocationNormalizedLoaded
  market: Market
  encryptedUserId?: string
  sheerIdUrl: string
  authenticated: boolean
  originUrl: string
}): NuxtLinkProps['to'] {
  const studentVerificationUrl = buildSheerIdUrl({
    currentRoute,
    market,
    encryptedUserId,
    sheerIdUrl,
    originUrl,
  })

  if (authenticated) return studentVerificationUrl

  return {
    name: ROUTES.AUTH.LOGIN,
    query: {
      type: 'external',
      bm_journey: 'student-discount',
      next: studentVerificationUrl,
    },
  }
}
